import { Investment } from '../types/Investment';
import { INVESTMENT_CONSTANTS } from '../constants';

export const investments: Investment[] = [
  {
    id: 'security-training',
    name: 'Security Awareness Training',
    icon: '🎓',
    description: 'Comprehensive cybersecurity training program designed to educate all employees on modern cyber threats, best practices for digital security, and incident response protocols. This program includes interactive workshops, simulated phishing exercises, and certification tracks that ensure your workforce becomes your strongest line of defense against cyber attacks. The training covers password management, social engineering awareness, secure communication practices, and regulatory compliance requirements.',
    cost: INVESTMENT_CONSTANTS.COST_TIER_2,
    impact: {
      growth: 5,
      stability: 15,
      riskReduction: 15,
      socialBenefit: 10
    },
    businessOutcomes: [
      'Enhanced Security Awareness',
      'Reduced Human Error Incidents', 
      'Improved Compliance Adherence',
      'Strengthened Security Culture'
    ],
    category: 'education'
  },
  {
    id: 'incident-response-team',
    name: 'Incident Response Team',
    icon: '🚨',
    description: 'Establish a dedicated cybersecurity incident response team operating around the clock to identify, contain, and mitigate security threats. This specialized team includes certified security analysts, forensic investigators, and threat intelligence experts who work together to minimize business disruption during security events. The team maintains documented response procedures, coordinates with law enforcement when necessary, and conducts post-incident reviews to strengthen your security posture.',
    cost: INVESTMENT_CONSTANTS.COST_TIER_2 * 4, // 400k
    impact: {
      growth: 0,
      stability: 25,
      riskReduction: 30,
      socialBenefit: 5
    },
    businessOutcomes: [
      'Rapid Threat Response',
      'Minimized Business Disruption',
      'Enhanced Security Posture',
      'Improved Incident Recovery'
    ],
    category: 'infrastructure'
  },
  {
    id: 'security-monitoring',
    name: 'Security Operations Center',
    icon: '🖥️',
    description: 'State-of-the-art Security Operations Center featuring advanced threat monitoring, real-time detection systems, and automated response capabilities. This comprehensive solution provides continuous visibility into your network infrastructure, applications, and user activities through cutting-edge SIEM technology, behavioral analytics, and threat intelligence feeds. The SOC operates 24/7 with expert analysts who proactively hunt for threats and coordinate rapid response to security incidents.',
    cost: INVESTMENT_CONSTANTS.COST_TIER_4 * 1.2, // 600k
    impact: {
      growth: 5,
      stability: 35,
      riskReduction: 40,
      socialBenefit: 5
    },
    businessOutcomes: [
      'Proactive Threat Detection',
      'Enhanced Network Visibility',
      'Reduced Security Incidents',
      'Improved Threat Intelligence'
    ],
    category: 'technology'
  },
  {
    id: 'backup-recovery',
    name: 'Backup & Recovery System',
    icon: '💾',
    description: 'Enterprise-grade backup and disaster recovery infrastructure designed to ensure business continuity during cyber attacks, natural disasters, or system failures. This solution includes automated daily backups, geographically distributed storage, rapid recovery capabilities, and regular testing procedures. The system maintains multiple recovery points, supports both on-premises and cloud environments, and includes comprehensive documentation and training for IT staff to execute recovery procedures efficiently.',
    cost: INVESTMENT_CONSTANTS.COST_TIER_2 * 3, // 300k
    impact: {
      growth: 0,
      stability: 30,
      riskReduction: 25,
      socialBenefit: 0
    },
    businessOutcomes: [
      'Ensured Business Continuity',
      'Rapid Data Recovery',
      'Minimized Downtime Risk',
      'Protected Critical Assets'
    ],
    category: 'infrastructure'
  },
  {
    id: 'cyber-insurance',
    name: 'Cyber Insurance Policy',
    icon: '🛡️',
    description: 'Comprehensive cyber liability insurance policy providing financial protection against the costs of data breaches, cyber attacks, and related business interruptions. This coverage includes legal fees, regulatory fines, notification costs, credit monitoring services, and business income protection. The policy also provides access to expert incident response services, forensic investigation support, and crisis communication assistance to help minimize reputational damage and accelerate recovery.',
    cost: INVESTMENT_CONSTANTS.COST_TIER_1,
    impact: {
      growth: 0,
      stability: 10,
      riskReduction: 5,
      socialBenefit: 5
    },
    businessOutcomes: [
      'Financial Risk Protection',
      'Reduced Liability Exposure',
      'Expert Crisis Support',
      'Accelerated Recovery Process'
    ],
    category: 'infrastructure'
  },
  {
    id: 'compliance-program',
    name: 'Compliance Program',
    icon: '📋',
    description: 'Comprehensive enterprise-wide security compliance and governance program designed to meet regulatory requirements and industry standards such as SOX, HIPAA, PCI-DSS, and ISO 27001. This program establishes clear security policies, procedures, and controls while providing ongoing compliance monitoring, risk assessments, and audit support. The program includes employee training, vendor assessments, and regular compliance reporting to demonstrate adherence to regulatory requirements.',
    cost: INVESTMENT_CONSTANTS.COST_TIER_2 * 2, // 200k
    impact: {
      growth: 0,
      stability: 20,
      riskReduction: 15,
      socialBenefit: 10
    },
    businessOutcomes: [
      'Regulatory Compliance Achievement',
      'Reduced Audit Risk',
      'Standardized Security Processes', 
      'Enhanced Governance Framework'
    ],
    category: 'infrastructure'
  },
  {
    id: 'zero-trust-architecture',
    name: 'Zero Trust Architecture',
    icon: '🔐',
    description: 'Revolutionary Zero Trust security architecture based on the principle of "never trust, always verify" that eliminates the traditional network perimeter model. This comprehensive approach requires verification for every user, device, and application attempting to access resources, regardless of location. The implementation includes multi-factor authentication, micro-segmentation, endpoint security, continuous monitoring, and adaptive access controls that dynamically adjust based on risk assessment and user behavior patterns.',
    cost: INVESTMENT_CONSTANTS.COST_TIER_4,
    impact: {
      growth: 0,
      stability: 40,
      riskReduction: 45,
      socialBenefit: 0
    },
    businessOutcomes: [
      'Maximum Security Resilience',
      'Eliminated Trust Assumptions',
      'Comprehensive Access Control',
      'Advanced Threat Prevention'
    ],
    category: 'technology'
  },
  {
    id: 'ai-threat-detection',
    name: 'AI-Powered Threat Detection',
    icon: '🤖',
    description: 'Cutting-edge AI-powered threat detection platform that leverages machine learning algorithms to identify sophisticated cyber threats that traditional security tools might miss. This advanced system analyzes network traffic patterns, user behaviors, and system activities to detect anomalies, zero-day attacks, and advanced persistent threats in real-time. The platform continuously learns from new threat data, adapts to evolving attack methods, and provides predictive insights to prevent future security incidents.',
    cost: INVESTMENT_CONSTANTS.COST_TIER_2 * 3.5, // 350k
    impact: {
      growth: 5,
      stability: 30,
      riskReduction: 35,
      socialBenefit: 0
    },
    businessOutcomes: [
      'Next-Generation Threat Detection',
      'Predictive Security Insights',
      'Automated Response Capabilities',
      'Enhanced Attack Prevention'
    ],
    category: 'technology'
  },
  {
    id: 'data-encryption',
    name: 'Data Encryption Platform',
    icon: '🔒',
    description: 'Comprehensive data encryption platform providing end-to-end security for all sensitive information at rest, in transit, and in use. This solution implements industry-standard encryption algorithms, secure key management, and automated encryption policies that protect critical business data across all systems and environments. The platform includes database encryption, file-level protection, email security, and secure communication channels while maintaining compliance with data protection regulations.',
    cost: INVESTMENT_CONSTANTS.COST_TIER_2 * 1.5, // 150k
    impact: {
      growth: 0,
      stability: 20,
      riskReduction: 30,
      socialBenefit: 5
    },
    businessOutcomes: [
      'Complete Data Protection',
      'Enhanced Privacy Compliance',
      'Secure Information Exchange',
      'Protected Intellectual Property'
    ],
    category: 'technology'
  },
  {
    id: 'crisis-response-team',
    name: 'Emergency Crisis Response Team',
    icon: '🚨',
    description: 'Specialized crisis management team with expertise in cybersecurity incidents, regulatory compliance, and stakeholder communication. This elite team includes former federal investigators, crisis communication specialists, legal experts, and reputation management professionals who can rapidly mobilize during security incidents. The team provides 24/7 crisis coordination, regulatory liaison services, media management, and strategic decision support to minimize business impact and accelerate recovery.',
    cost: INVESTMENT_CONSTANTS.COST_TIER_4 * 1.6, // 800k
    impact: {
      growth: 0,
      stability: 50,
      riskReduction: 45,
      socialBenefit: 15
    },
    businessOutcomes: [
      'Rapid Crisis Coordination',
      'Expert Incident Management',
      'Regulatory Compliance Support',
      'Stakeholder Communication'
    ],
    category: 'crisis-management'
  },
  {
    id: 'reputation-recovery',
    name: 'Reputation Recovery Program',
    icon: '📈',
    description: 'Comprehensive reputation management and brand recovery program designed to rebuild stakeholder trust after security incidents. This program combines strategic public relations, social media management, thought leadership initiatives, and customer outreach campaigns. The service includes crisis communication planning, media training for executives, proactive stakeholder engagement, and long-term brand rehabilitation strategies to restore market confidence and competitive positioning.',
    cost: INVESTMENT_CONSTANTS.COST_TIER_4 * 1.2, // 600k
    impact: {
      growth: 15,
      stability: 35,
      riskReduction: 20,
      socialBenefit: 25
    },
    businessOutcomes: [
      'Brand Trust Restoration',
      'Stakeholder Confidence',
      'Market Position Recovery',
      'Enhanced Corporate Image'
    ],
    category: 'crisis-management'
  },
  {
    id: 'legal-defense-fund',
    name: 'Legal Defense & Settlement Fund',
    icon: '⚖️',
    description: 'Dedicated legal defense fund and expert legal team specializing in cybersecurity litigation, regulatory investigations, and crisis-related legal challenges. This comprehensive legal package includes class-action defense, regulatory compliance support, settlement negotiation services, and ongoing legal counsel. The fund provides financial resources for extended legal battles while the expert team ensures optimal legal strategy execution and regulatory compliance across multiple jurisdictions.',
    cost: INVESTMENT_CONSTANTS.COST_TIER_5,
    impact: {
      growth: 0,
      stability: 40,
      riskReduction: 35,
      socialBenefit: 10
    },
    businessOutcomes: [
      'Legal Risk Mitigation',
      'Regulatory Compliance',
      'Settlement Optimization',
      'Litigation Management'
    ],
    category: 'crisis-management'
  },
  {
    id: 'customer-retention-program',
    name: 'Customer Retention & Trust Program',
    icon: '🤝',
    description: 'Strategic customer retention program focused on rebuilding trust and preventing customer churn during and after security incidents. This comprehensive initiative includes personalized customer outreach, enhanced support services, service level guarantees, compensation programs, and loyalty incentives. The program also provides identity monitoring services for affected customers, enhanced security features, and transparent communication channels to demonstrate ongoing commitment to customer protection.',
    cost: INVESTMENT_CONSTANTS.COST_TIER_4 * 1.5, // 750k
    impact: {
      growth: 25,
      stability: 30,
      riskReduction: 15,
      socialBenefit: 35
    },
    businessOutcomes: [
      'Reduced Customer Churn',
      'Enhanced Customer Loyalty',
      'Trust Rebuilding',
      'Service Excellence'
    ],
    category: 'crisis-management'
  },
  {
    id: 'forensic-investigation',
    name: 'Advanced Forensic Investigation Services',
    icon: '🔍',
    description: 'Elite cybersecurity forensics team specializing in advanced persistent threat investigation, digital evidence collection, and attack attribution. This service provides comprehensive incident analysis, malware reverse engineering, network traffic analysis, and threat intelligence gathering. The team works with law enforcement agencies, coordinates with international cybersecurity organizations, and provides detailed forensic reports that can be used for legal proceedings and insurance claims.',
    cost: INVESTMENT_CONSTANTS.COST_TIER_2 * 4.5, // 450k
    impact: {
      growth: 0,
      stability: 25,
      riskReduction: 50,
      socialBenefit: 20
    },
    businessOutcomes: [
      'Root Cause Analysis',
      'Evidence Preservation',
      'Attack Attribution',
      'Legal Documentation'
    ],
    category: 'crisis-management'
  },
  {
    id: 'regulatory-compliance-accelerator',
    name: 'Regulatory Compliance Accelerator',
    icon: '📋',
    description: 'Specialized regulatory compliance program designed to meet multi-jurisdictional requirements following security incidents. This service includes GDPR compliance acceleration, SEC reporting assistance, state and federal regulatory coordination, and international compliance support. The program provides dedicated compliance officers, automated reporting systems, policy development, and ongoing regulatory monitoring to ensure full compliance across all relevant jurisdictions while minimizing regulatory penalties.',
    cost: INVESTMENT_CONSTANTS.COST_TIER_4 * 1.1, // 550k
    impact: {
      growth: 0,
      stability: 45,
      riskReduction: 40,
      socialBenefit: 30
    },
    businessOutcomes: [
      'Multi-Jurisdiction Compliance',
      'Regulatory Risk Reduction',
      'Penalty Minimization',
      'Ongoing Compliance Monitoring'
    ],
    category: 'crisis-management'
  },
  {
    id: 'executive-protection',
    name: 'Executive Protection & Communications',
    icon: '👥',
    description: 'Comprehensive executive protection program combining physical security, digital security, and crisis communications training. This service includes executive security assessments, secure communications systems, crisis management training, media preparation, and personal protection services. The program ensures executive safety during high-profile security incidents while preparing leadership for effective crisis communication and stakeholder management throughout the incident response process.',
    cost: INVESTMENT_CONSTANTS.COST_TIER_2 * 3, // 300k
    impact: {
      growth: 5,
      stability: 30,
      riskReduction: 25,
      socialBenefit: 15
    },
    businessOutcomes: [
      'Executive Safety Assurance',
      'Crisis Leadership Readiness',
      'Secure Communications',
      'Media Preparation'
    ],
    category: 'crisis-management'
  }
];

export const TOTAL_BUDGET = INVESTMENT_CONSTANTS.DEFAULT_BUDGET * 2; // $2 million
export const MAX_SELECTIONS = 3; // This could be moved to constants if needed